import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Download,
  Users,
  FileText,
  DollarSign,
  Building2,
  Sparkles,
  FileSpreadsheet,
  CheckCircle
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DESCARGAS = [
  {
    id: 'aliados',
    titulo: 'Base de Aliados',
    descripcion: 'Lista completa de aliados con datos de contacto y bancarios',
    icon: Building2,
    color: 'blue',
    endpoint: '/api/descargas/aliados',
    filename: 'aliados.csv'
  },
  {
    id: 'referidos',
    titulo: 'Base de Referidos',
    descripcion: 'Todos los referidos con aliado asociado, estado y comisiones',
    icon: Users,
    color: 'green',
    endpoint: '/api/descargas/referidos',
    filename: 'referidos.csv'
  },
  {
    id: 'comisiones',
    titulo: 'Reporte de Comisiones',
    descripcion: 'Detalle de comisiones generadas, pendientes y pagadas',
    icon: DollarSign,
    color: 'yellow',
    endpoint: '/api/descargas/comisiones',
    filename: 'comisiones.csv'
  },
  {
    id: 'cuentas-cobro',
    titulo: 'Cuentas de Cobro',
    descripcion: 'Historial de cuentas de cobro generadas',
    icon: FileText,
    color: 'purple',
    endpoint: '/api/descargas/cuentas-cobro',
    filename: 'cuentas_cobro.csv'
  }
];

const DescargasAdmin = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [downloading, setDownloading] = useState(null);
  const [downloaded, setDownloaded] = useState([]);

  const handleDownload = async (descarga) => {
    setDownloading(descarga.id);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}${descarga.endpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Crear URL y descargar
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', descarga.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`${descarga.titulo} descargado`);
      setDownloaded(prev => [...prev, descarga.id]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al descargar');
    } finally {
      setDownloading(null);
    }
  };

  // Verificar permisos
  if (user?.role !== 'gerencia' && user?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center text-white">
        <div className="text-center">
          <FileSpreadsheet className="w-16 h-16 mx-auto text-zinc-700 mb-4" />
          <h2 className="text-xl font-display mb-2">Acceso Restringido</h2>
          <p className="text-zinc-400">Solo administradores pueden acceder a las descargas</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Header */}
      <div className="relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{ backgroundImage: 'url(/images/novio-mascota.png)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-950/30 via-zinc-950/80 to-zinc-950" />
        
        <header className="relative bg-transparent">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-20">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate('/aliados')}
                  className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                  <span className="hidden sm:inline">Aliados</span>
                </button>
                <div className="h-6 w-px bg-zinc-700" />
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-[#D4AF37]" />
                  <h1 className="font-display text-xl">Centro de Descargas</h1>
                </div>
              </div>
            </div>
          </div>
        </header>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
          <p className="text-zinc-400 text-center">
            Descarga los reportes en formato CSV para análisis en Excel
          </p>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid md:grid-cols-2 gap-6">
          {DESCARGAS.map((descarga) => {
            const Icon = descarga.icon;
            const isDownloading = downloading === descarga.id;
            const isDownloaded = downloaded.includes(descarga.id);
            
            const colorClasses = {
              blue: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
              green: 'bg-green-500/20 text-green-400 border-green-500/30',
              yellow: 'bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/30',
              purple: 'bg-purple-500/20 text-purple-400 border-purple-500/30'
            };
            
            return (
              <div
                key={descarga.id}
                className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 hover:border-zinc-700 transition-all"
              >
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-xl border ${colorClasses[descarga.color]}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  
                  <div className="flex-1">
                    <h3 className="font-display text-lg mb-1">{descarga.titulo}</h3>
                    <p className="text-sm text-zinc-400 mb-4">{descarga.descripcion}</p>
                    
                    <button
                      onClick={() => handleDownload(descarga)}
                      disabled={isDownloading}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all text-sm font-medium ${
                        isDownloaded
                          ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                          : 'bg-[#D4AF37] text-black hover:bg-[#C49F27]'
                      }`}
                    >
                      {isDownloading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                          Descargando...
                        </>
                      ) : isDownloaded ? (
                        <>
                          <CheckCircle className="w-4 h-4" />
                          Descargado
                        </>
                      ) : (
                        <>
                          <Download className="w-4 h-4" />
                          Descargar CSV
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Info */}
        <div className="mt-8 p-4 bg-zinc-900/30 border border-zinc-800 rounded-xl">
          <p className="text-sm text-zinc-400 text-center">
            <FileSpreadsheet className="w-4 h-4 inline mr-2" />
            Los archivos CSV se pueden abrir directamente en Excel, Google Sheets o cualquier hoja de cálculo
          </p>
        </div>
      </main>
    </div>
  );
};

export default DescargasAdmin;
